import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import type { AnimalOption } from '@rehome/shared';

interface SearchableAnimalSelectProps {
  options: AnimalOption[];
  /** `null` = nincs szűrés (minden állat). */
  value: string | null;
  onChange: (animalId: string | null) => void;
  allLabel?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Kereshető állatválasztó.
 *
 * Egy sima `<select>` néhány tucat állatnál még működik, de egy több százas
 * menhelynél görgetéssel kell megkeresni a nevet — pont akkor, amikor a
 * dolgozó tudja, kit keres. Ez a mező gépelésre szűkít.
 *
 * ── Miért nem jelenít meg mindent egyszerre ────────────────────────────────
 * A találatok száma korlátozott: több ezer `<li>` kirajzolása minden egyes
 * leütésnél érezhetően akadna. Aki nem találja az elsőre a nevét, gépel még
 * egy betűt — az olcsóbb, mint a teljes lista folyamatos újrarajzolása.
 */
export const SearchableAnimalSelect: React.FC<SearchableAnimalSelectProps> = ({
  options, value, onChange, allLabel = 'Minden állat', placeholder = 'Keresés név szerint…', className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_VISIBLE = 50;

  const selected = useMemo(
    () => options.find(o => o.id === value) || null,
    [options, value]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, MAX_VISIBLE);
    // Az ékezetes nevek miatt egyszerű `includes` — a magyar helyesírás
    // szerint keresők úgyis ékezettel gépelnek, a `localeCompare`-alapú
    // normalizálás pedig minden leütésnél végigfutna az egész listán.
    return options.filter(o => o.name.toLowerCase().includes(q)).slice(0, MAX_VISIBLE);
  }, [options, query]);

  const hiddenCount = useMemo(() => {
    const q = query.trim().toLowerCase();
    const total = q ? options.filter(o => o.name.toLowerCase().includes(q)).length : options.length;
    return Math.max(0, total - matches.length);
  }, [options, query, matches.length]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  // Kattintás a mezőn kívülre bezárja — enélkül a lenyíló lista ottragadna
  // az oldal többi eleme fölött.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const choose = (id: string | null) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, matches.length)); // +1: az "összes" sor
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight === 0) choose(null);
      else choose(matches[highlight - 1]?.id ?? null);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? 'text-gray-900 truncate' : 'text-gray-500'}>
          {selected ? selected.name : allLabel}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Szűrés törlése"
              onClick={e => { e.stopPropagation(); choose(null); }}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[16rem] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </div>

          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onMouseEnter={() => setHighlight(0)}
                onClick={() => choose(null)}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between ${highlight === 0 ? 'bg-teal-50 text-teal-800' : 'text-gray-700'}`}
              >
                {allLabel}
                {value === null && <Check className="w-3.5 h-3.5" />}
              </button>
            </li>

            {matches.map((o, i) => (
              <li key={o.id}>
                <button
                  type="button"
                  onMouseEnter={() => setHighlight(i + 1)}
                  onClick={() => choose(o.id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 ${highlight === i + 1 ? 'bg-teal-50 text-teal-800' : 'text-gray-700'}`}
                >
                  <span className="truncate">{o.name}</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    {o.species && <span className="text-[11px] text-gray-400">{o.species}</span>}
                    {value === o.id && <Check className="w-3.5 h-3.5" />}
                  </span>
                </button>
              </li>
            ))}

            {matches.length === 0 && (
              <li className="px-3 py-3 text-sm text-gray-400 text-center">Nincs találat.</li>
            )}
            {hiddenCount > 0 && (
              <li className="px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
                További {hiddenCount} találat — pontosíts a kereséssel.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
